import { useAtom } from 'jotai';
import { RefObject, useCallback, useEffect } from 'react';

import {
  v2IsFullscreenAtom,
  v2IsImmersiveAtom,
} from '../../store/v2/viewer.atom';

interface FullscreenElement extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void> | void;
}

interface FullscreenDocument extends Document {
  webkitExitFullscreen?: () => Promise<void> | void;
  webkitFullscreenElement?: Element | null;
}

const isFullscreenSupported = () => {
  if (typeof document === 'undefined') {
    return false;
  }

  return (
    document.fullscreenEnabled ||
    'webkitRequestFullscreen' in document.documentElement
  );
};

/**
 * 브라우저 Fullscreen API 래퍼.
 * iPhone Safari처럼 요소 전체화면을 지원하지 않는 환경에서는
 * 몰입 모드(v2IsImmersiveAtom)로 폴백한다.
 */
const useFullscreen = (targetRef: RefObject<HTMLElement>) => {
  const [isFullscreen, setIsFullscreen] = useAtom(v2IsFullscreenAtom);
  const [isImmersive, setIsImmersive] = useAtom(v2IsImmersiveAtom);

  useEffect(() => {
    const doc = document as FullscreenDocument;

    const handleFullscreenChange = () => {
      const fullscreenElement =
        doc.fullscreenElement ?? doc.webkitFullscreenElement;
      setIsFullscreen(Boolean(fullscreenElement));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener(
        'webkitfullscreenchange',
        handleFullscreenChange,
      );
    };
  }, [setIsFullscreen]);

  const toggleFullscreen = useCallback(() => {
    if (!isFullscreenSupported()) {
      setIsImmersive((prev) => !prev);
      return;
    }

    const doc = document as FullscreenDocument;
    const isActive = Boolean(
      doc.fullscreenElement ?? doc.webkitFullscreenElement,
    );

    if (isActive) {
      if (doc.exitFullscreen) {
        doc.exitFullscreen();
      } else {
        doc.webkitExitFullscreen?.();
      }
      return;
    }

    const target = targetRef.current as FullscreenElement | null;
    if (!target) {
      return;
    }

    if (target.requestFullscreen) {
      target.requestFullscreen();
    } else {
      target.webkitRequestFullscreen?.();
    }
  }, [setIsImmersive, targetRef]);

  return {
    isFullscreenActive: isFullscreen || isImmersive,
    isImmersive,
    toggleFullscreen,
  };
};

export default useFullscreen;
